// modules/shared/src/services/sentryAxiosFingerprint.js
// CommonJS so the webpack build imports it AND plain `node` can require() it
// for the standalone verify script (sentryAxiosFingerprint.verify.js).

const { isHttpErrorSurfaceWired } = require('./unhandledHttpErrors')

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
const NUMERIC_RE = /^\d+$/
const HEX24_RE = /^[0-9a-fA-F]{24}$/

function normalizeUrlPath(url) {
  if (!url) return 'unknown'
  let path
  try {
    // base lets relative urls parse; we keep only the pathname (drops query/hash)
    path = new URL(url, 'http://_').pathname
  } catch (e) {
    path = String(url).split('?')[0].split('#')[0]
  }
  const normalized = path
    .split('/')
    .map(seg => (UUID_RE.test(seg) || NUMERIC_RE.test(seg) || HEX24_RE.test(seg) ? '{id}' : seg))
    .join('/')
  return normalized || '/'
}

function axiosFingerprint(error) {
  if (!error || error.isAxiosError !== true) return null
  const config = error.config
  const response = error.response
  if (!config && !response) return null
  const method = (config && config.method ? String(config.method) : 'unknown').toUpperCase()
  const path = normalizeUrlPath(config && config.url)
  const status = response && response.status ? String(response.status) : 'no-response'
  return ['axios', method, path, status]
}

// An uncaught axios HTTP rejection is no longer truly unhandled: the shared
// unhandledrejection surface (unhandledHttpErrors.js) toasts it. Relabel the
// mechanism so routine 4xx/5xx stop showing as unhandled exceptions in Sentry,
// while the event (and its endpoint+status grouping) still reports. Network
// errors keep mechanism untouched — the toast surface skips them too.
// Gate: only relabel when a real toast surface is actually wired. Without one
// the user got no feedback, so "handled" would be a lie that hides the failure.
function markUnhandledAxiosRejectionHandled(event, error) {
  if (!isHttpErrorSurfaceWired()) return
  const fp = axiosFingerprint(error)
  if (!fp || fp[3] === 'no-response') return
  const values = (event && event.exception && event.exception.values) || []
  for (const value of values) {
    const mechanism = value && value.mechanism
    if (
      mechanism &&
      mechanism.handled === false &&
      /onunhandledrejection/.test(String(mechanism.type || ''))
    ) {
      mechanism.handled = true
      mechanism.data = Object.assign({}, mechanism.data, { portalHttpErrorSurface: true })
    }
  }
}

function axiosFingerprintProcessor(event, hint) {
  try {
    const originalException = hint && hint.originalException
    const fp = axiosFingerprint(originalException)
    if (fp) {
      event.fingerprint = fp
      event.tags = Object.assign({}, event.tags, {
        'axios.endpoint': `${fp[1]} ${fp[2]}`,
        'axios.status': fp[3],
      })
      markUnhandledAxiosRejectionHandled(event, originalException)
    }
  } catch (e) {
    // never let enrichment drop an event
  }
  return event
}

module.exports = {
  normalizeUrlPath,
  axiosFingerprint,
  axiosFingerprintProcessor,
  markUnhandledAxiosRejectionHandled,
}
